import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../App";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import { 
  PieChart, Pie, Cell, ResponsiveContainer, 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend 
} from "recharts";
import { motion } from "framer-motion";
import { 
  FileText, TrendingUp, AlertTriangle, CheckCircle, 
  Clock, Euro, Upload, RefreshCw, Calendar, Settings
} from "lucide-react";
import ImportContractsModal from "../components/ImportContractsModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COLORS = ['#3b82f6', '#06b6d4', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6'];

const formatCurrency = (value) => {
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value);
};

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  return date.toLocaleDateString('fr-FR', { 
    day: '2-digit', 
    month: '2-digit', 
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'Europe/Paris'
  });
};

export default function ContratsDashboard() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [deadlines, setDeadlines] = useState(null);
  const [loading, setLoading] = useState(true);
  const [importModalOpen, setImportModalOpen] = useState(false);

  const fetchData = async () => {
    if (!token) return;
    setLoading(true);
    
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [statsRes, deadlinesRes] = await Promise.all([
        axios.get(`${API}/contracts/stats/overview`, { headers }),
        axios.get(`${API}/contracts/stats/deadlines?months=6`, { headers })
      ]);
      
      setStats(statsRes.data);
      setDeadlines(deadlinesRes.data);
    } catch (error) {
      console.error("Error fetching contract stats:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [token]);

  const handleImportSuccess = () => {
    fetchData();
  };

  if (loading) {
    return (
      <div className="space-y-4 sm:space-y-6">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
          <div className="h-8 w-48 skeleton rounded"></div>
          <div className="h-10 w-32 skeleton rounded"></div>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          {[...Array(4)].map((_, i) => <div key={i} className="h-24 skeleton rounded-lg"></div>)}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
          <div className="h-72 sm:h-80 skeleton rounded-lg"></div>
          <div className="h-72 sm:h-80 skeleton rounded-lg"></div>
        </div>
      </div>
    );
  }

  const pieData = stats?.by_type?.map((t, i) => ({
    name: t.type,
    value: t.mrr,
    count: t.count,
    color: COLORS[i % COLORS.length]
  })) || [];

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="contrats-dashboard">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-foreground">
            Gestion des Contrats
          </h1>
          <p className="text-muted-foreground text-sm">
            {stats?.last_import ? `Import: ${formatDate(stats.last_import)}` : 'Aucun import'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={fetchData}>
            <RefreshCw className="w-4 h-4 sm:mr-2" />
            <span className="hidden sm:inline">Actualiser</span>
          </Button>
          <Button 
            variant="outline"
            size="sm"
            onClick={() => navigate('/contrats/parametres')}
            title="Paramètres"
            data-testid="contrats-settings-btn"
          >
            <Settings className="w-4 h-4" />
          </Button>
          <Button 
            onClick={() => setImportModalOpen(true)}
            className="btn-primary bg-primary text-primary-foreground"
            data-testid="import-contracts-btn"
            size="sm"
          >
            <Upload className="w-4 h-4 sm:mr-2" />
            <span className="hidden sm:inline">Importer</span>
            <span className="sm:hidden">Import</span>
          </Button>
        </div>
      </div>

      {/* Alert Banner for expiring contracts */}
      {deadlines && deadlines.expiring_soon_count > 0 && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <Card className="bg-orange-50 border-orange-200">
            <CardContent className="p-3 sm:p-4 flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4">
              <div className="p-2 sm:p-3 rounded-full bg-orange-100">
                <AlertTriangle className="w-4 sm:w-5 h-4 sm:h-5 text-orange-600" />
              </div>
              <div className="flex-1">
                <p className="font-semibold text-foreground text-sm sm:text-base">
                  {deadlines.expiring_soon_count} contrat{deadlines.expiring_soon_count > 1 ? 's' : ''} arrive{deadlines.expiring_soon_count > 1 ? 'nt' : ''} à échéance
                </p>
                <p className="text-xs sm:text-sm text-orange-700">
                  {formatCurrency(deadlines.expiring_soon_mrr)}/mois de MRR
                </p>
              </div>
              <Button 
                variant="outline" 
                className="border-orange-300 text-orange-700 hover:bg-orange-100 w-full sm:w-auto"
                size="sm"
                onClick={() => navigate('/contrats/echeances')}
              >
                Voir les détails
              </Button>
            </CardContent>
          </Card>
        </motion.div>
      )}

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <Card className="bg-white border">
          <CardContent className="p-3 sm:p-4 flex items-center gap-3 sm:gap-4">
            <div className="p-2 sm:p-3 rounded-lg bg-blue-50">
              <FileText className="w-4 sm:w-5 h-4 sm:h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-xs uppercase tracking-wider text-muted-foreground">Contrats actifs</p>
              <p className="text-2xl font-bold text-foreground">{stats?.active_contracts || 0}</p>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-white border">
          <CardContent className="p-3 sm:p-4 flex items-center gap-3 sm:gap-4">
            <div className="p-2 sm:p-3 rounded-lg bg-green-50">
              <Euro className="w-4 sm:w-5 h-4 sm:h-5 text-green-600" />
            </div>
            <div>
              <p className="text-[10px] sm:text-xs uppercase tracking-wider text-muted-foreground">MRR Total</p>
              <p className="text-lg sm:text-2xl font-bold text-foreground">{formatCurrency(stats?.total_mrr || 0)}</p>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-white border">
          <CardContent className="p-3 sm:p-4 flex items-center gap-3 sm:gap-4">
            <div className="p-2 sm:p-3 rounded-lg bg-cyan-50">
              <TrendingUp className="w-4 sm:w-5 h-4 sm:h-5 text-cyan-600" />
            </div>
            <div>
              <p className="text-[10px] sm:text-xs uppercase tracking-wider text-muted-foreground">CA Leaser</p>
              <p className="text-lg sm:text-2xl font-bold text-foreground">{formatCurrency(stats?.total_ca_leaser || 0)}</p>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-white border">
          <CardContent className="p-3 sm:p-4 flex items-center gap-3 sm:gap-4">
            <div className="p-2 sm:p-3 rounded-lg bg-orange-50">
              <Clock className="w-4 sm:w-5 h-4 sm:h-5 text-orange-500" />
            </div>
            <div>
              <p className="text-[10px] sm:text-xs uppercase tracking-wider text-muted-foreground">Bientôt expirés</p>
              <p className="text-lg sm:text-2xl font-bold text-orange-500">{stats?.expiring_soon || 0}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        {/* MRR by Type */}
        <Card className="bg-white border">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm sm:text-base font-semibold">MRR par Type de Contrat</CardTitle>
          </CardHeader>
          <CardContent className="p-2 sm:p-6">
            {pieData.length > 0 ? (
              <div className="flex flex-col sm:flex-row items-center">
                <div className="w-full sm:w-1/2 h-48 sm:h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={pieData}
                        cx="50%"
                        cy="50%"
                        innerRadius={40}
                        outerRadius={70}
                        dataKey="value"
                        label={({ name, percent }) => `${(percent * 100).toFixed(0)}%`}
                      >
                        {pieData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip 
                        formatter={(value) => formatCurrency(value)}
                        contentStyle={{ borderRadius: '8px' }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="w-1/2 space-y-2">
                  {pieData.map((entry, i) => (
                    <div key={i} className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: entry.color }} />
                        <span className="text-foreground">{entry.name}</span>
                      </div>
                      <span className="font-mono font-medium">{formatCurrency(entry.value)}/mois</span>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="h-64 flex items-center justify-center text-muted-foreground">
                Aucune donnée disponible
              </div>
            )}
          </CardContent>
        </Card>

        {/* Expiring Contracts */}
        <Card className="bg-white border">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold flex items-center gap-2">
              <Calendar className="w-4 h-4 text-orange-500" />
              Prochaines échéances
            </CardTitle>
          </CardHeader>
          <CardContent>
            {deadlines?.expiring_soon?.length > 0 ? (
              <div className="space-y-3 max-h-64 overflow-y-auto">
                {deadlines.expiring_soon.slice(0, 6).map((contract, i) => (
                  <div key={i} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <p className="font-medium text-foreground">{contract.societe}</p>
                      <p className="text-xs text-muted-foreground">{contract.produit}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-mono font-bold text-foreground">{formatCurrency(contract.montant_abonnement)}/mois</p>
                      <p className="text-xs text-orange-600">
                        {new Date(contract.date_fin).toLocaleDateString('fr-FR', { timeZone: 'Europe/Paris' })}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="h-64 flex flex-col items-center justify-center text-muted-foreground">
                <CheckCircle className="w-12 h-12 text-green-500 mb-2" />
                <p>Aucune échéance proche</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Abonnement Breakdown */}
      <Card className="bg-white border">
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-semibold">Répartition Abonnements</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div>
                <div className="flex justify-between mb-1">
                  <span className="text-sm text-muted-foreground">Gestion Interne</span>
                  <span className="text-sm font-mono font-bold">{formatCurrency(stats?.mrr_gestion_interne || 0)}/mois</span>
                </div>
                <Progress 
                  value={stats?.total_mrr > 0 ? (stats.mrr_gestion_interne / stats.total_mrr) * 100 : 0} 
                  className="h-3 [&>div]:bg-green-500" 
                />
              </div>
              <div>
                <div className="flex justify-between mb-1">
                  <span className="text-sm text-muted-foreground">Finance (Leasing)</span>
                  <span className="text-sm font-mono font-bold">{formatCurrency(stats?.mrr_finance || 0)}/mois</span>
                </div>
                <Progress 
                  value={stats?.total_mrr > 0 ? (stats.mrr_finance / stats.total_mrr) * 100 : 0} 
                  className="h-3 [&>div]:bg-orange-500" 
                />
              </div>
            </div>
            <div className="flex items-center justify-center">
              <div className="text-center">
                <p className="text-sm text-muted-foreground mb-1">Total MRR</p>
                <p className="text-4xl font-bold text-foreground">{formatCurrency(stats?.total_mrr || 0)}</p>
                <p className="text-sm text-muted-foreground">par mois</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Import Modal */}
      <ImportContractsModal 
        isOpen={importModalOpen}
        onClose={() => setImportModalOpen(false)}
        onSuccess={handleImportSuccess}
      />
    </div>
  );
}
